import type { LawFirmContactListEntry } from '@common/types/contacts';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { type Column } from '@/components/DataTable';
import PageWrapper from '@/components/PageWrapper';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useContactsByLawFirmId } from '@/queries/contacts';
import ProspectCellLink from './ProspectCellLink';

const SEARCH_DEBOUNCE_MS = 300;

export default function Contacts() {
    const { activeLawFirmId: lawFirmId } = useLawFirmContext();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchKey, setSearchKey] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(searchKey.trim());
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [searchKey]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, lawFirmId]);

    const { data, isLoading, isFetching } = useContactsByLawFirmId({
        lawFirmId,
        page,
        pageSize: rowsPerPage,
        search: debouncedSearch || undefined,
        enabled: !!lawFirmId,
    });

    const contacts = data?.items ?? [];
    const total = data?.total ?? 0;

    useEffect(() => {
        if (rowsPerPage < 0) {
            if (page !== 0) setPage(0);
            return;
        }
        const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [total, rowsPerPage, page]);

    const columns: Column<LawFirmContactListEntry>[] = [
        {
            id: 'contactName',
            label: 'Name',
            _render: (contact) => (
                <Link style={{ fontWeight: 500 }} to={`/contacts/${contact.contactId}`}>
                    {contact.contactName}
                </Link>
            ),
        },
        {
            id: 'prospect',
            label: 'Prospect',
            _render: (contact) => (
                <ProspectCellLink
                    prospectId={contact.prospect.prospectId}
                    prospectName={contact.prospect.prospectName}
                    customFileNumber={contact.prospect.customFileNumber}
                />
            ),
        },
        {
            id: 'contactEmail',
            label: 'Email',
        },
        {
            id: 'phoneNumber',
            label: 'Phone',
            _render: (contact) => contact.phoneNumber ?? '',
        },
    ];

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <PageWrapper title="Contacts">
            <Stack spacing={2}>
                <TextField
                    size="small"
                    placeholder="Search by name, email, phone, or prospect"
                    value={searchKey}
                    onChange={(event) => {
                        setSearchKey(event.target.value);
                    }}
                    sx={{ maxWidth: 420 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <DataTable
                    generateKeyFrom="contactId"
                    isLoading={isLoading || (isFetching && contacts.length === 0)}
                    columns={columns}
                    rows={contacts}
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={(_ev, newPage) => setPage(newPage)}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Stack>
        </PageWrapper>
    );
}
